import api from "../../services/api";
import type {
  PreviewMatchesRequest,
  PreviewMatchesResponse,
  GenerateMatchesRequest,
  GenerateMatchesResponse,
  FormatMatchesResponse,
  UpdateMatchRequest,
  TournamentMatch,
  MatchDetailResponse,
  MatchLineups,
  SaveLineupRequest,
  SubstitutionRequest,
} from "./types";

export interface PublishMatchesResponse {
  updatedCount: number;
}

export const matchesApi = {
  /**
   * Preview matches schedule without saving to the database
   * Generates a preview of all matches including TBD matches for knockout formats
   */
  previewMatches: async (data: PreviewMatchesRequest): Promise<PreviewMatchesResponse> => {
    return api.post("/matches/preview", data);
  },

  /**
   * Generate matches for all formats in a tournament
   * Automatically retrieves format teams, rules, and constraints to generate matches
   * Any existing matches for the tournament formats will be deleted and regenerated
   */
  generateMatches: async (data: GenerateMatchesRequest): Promise<GenerateMatchesResponse> => {
    return api.post("/matches/generate", data);
  },

  /**
   * Get all matches associated with a format block
   */
  getMatchesByFormat: async (formatId: string): Promise<FormatMatchesResponse> => {
    return api.get(`/matches/format/${formatId}`);
  },

  /**
   * Publish matches (draft → scheduled)
   * Makes matches visible to players
   */
  publishMatches: async (formatId: string): Promise<PublishMatchesResponse> => {
    return api.post(`/matches/format/${formatId}/publish`);
  },

  /**
   * Unpublish matches (scheduled → draft)
   * Cannot unpublish if any matches are in_progress or completed
   */
  unpublishMatches: async (formatId: string): Promise<PublishMatchesResponse> => {
    return api.post(`/matches/format/${formatId}/unpublish`);
  },

  /**
   * Update a single match (date, time, venue)
   */
  updateMatch: async (matchId: string, data: UpdateMatchRequest): Promise<TournamentMatch> => {
    return api.patch(`/matches/${matchId}`, data);
  },

  /**
   * Get comprehensive match details including team players, forms, and recent matches
   */
  getMatchDetails: async (matchId: string): Promise<MatchDetailResponse> => {
    return api.get(`/matches/${matchId}/details`);
  },

  /**
   * The match's team sheets: who started, who was benched, and when they changed
   */
  getMatchLineups: async (matchId: string): Promise<MatchLineups> => {
    return api.get(`/matches/${matchId}/lineups`);
  },

  /**
   * Replace one club's team sheet. Returns both clubs' sheets.
   */
  saveLineup: async (matchId: string, data: SaveLineupRequest): Promise<MatchLineups> => {
    return api.put(`/matches/${matchId}/lineups`, data);
  },

  /**
   * One player off, one on, at the same minute. Returns both clubs' sheets.
   */
  substitute: async (matchId: string, data: SubstitutionRequest): Promise<MatchLineups> => {
    return api.post(`/matches/${matchId}/substitutions`, data);
  },

  /**
   * Delete all matches for a tournament
   * Deletes all matches across all format blocks in the tournament
   */
  deleteMatches: async (tournamentId: string): Promise<void> => {
    return api.delete(`/matches/tournament/${tournamentId}`);
  },
};

